import React from 'react';
import { Panel } from '@/components/ui/panel';
import { Box, HStack, Icon, VStack } from '@chakra-ui/react';
import { DocumentWithDownloadUrl as Document } from '@/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import DocumentTags from './DocumentTags';
import DocumentSummary from './DocumentSummary';
import DocumentDetails from './DocumentDetails';
import { isImportant, isNote } from '@/utils/documentUtils';
import { useHousePermissions } from '@/hooks/useHasPermission';
import { useAuth } from '@/context/AuthContext.tsx';
import SharingVisibilitySwitch, { VisibilityCaptionVariant } from '@/components/documents/SharingVisibilitySwitch.tsx';
import { updateDocumentVisibilityHousesHouseIdDocumentsDocumentIdVisibilityPatchMutation } from '@/client/@tanstack/react-query.gen.ts';
import { houseDocumentKeys } from '@/hooks/house/documents/queries/queryKeys.ts';
import { toaster } from '@/components/ui/toaster';
import { LuLock, LuLockOpen } from 'react-icons/lu';
import { Title } from '@/components/ui/title';

interface BaseDocumentEditProps {
	document: Document;
	children?: React.ReactNode;
	onVisibilityChange?: (isPrivate: boolean) => void;
	/**
	 * A pending share isn't in this house's pipeline (it's only cloned on acceptance), so its
	 * summary/tags come from the share payload and must render without consulting pipeline status —
	 * otherwise they'd read as "Processing" forever. Passed down to those sections.
	 */
	isPendingShare?: boolean;
}

/**
 * Base component for document edit content
 * Provides common document information display
 */
const BaseDocumentEdit: React.FC<BaseDocumentEditProps> = ({
	document,
	children,
	onVisibilityChange,
	isPendingShare = false,
}) => {
	const showSummary = !isNote(document) && !isImportant(document);
	const { isSuperAdmin } = useAuth();
	const { canSetPrivateDocuments } = useHousePermissions();
	const canSetPrivate = canSetPrivateDocuments || isSuperAdmin;

	const queryClient = useQueryClient();

	const { mutate: updateVisibility } = useMutation({
		...updateDocumentVisibilityHousesHouseIdDocumentsDocumentIdVisibilityPatchMutation(),
		onMutate: (variables) => {
			// Keep the drawer toggle feeling instant, matching the table shortcut, while preserving rollback on failure.
			const listKey = { queryKey: houseDocumentKeys.list(document.house_id) };
			const previous = queryClient.getQueriesData<Document[]>(listKey);
			queryClient.setQueriesData(listKey, (oldData: Document[] | undefined) =>
				oldData?.map((doc) => (doc.id === document.id ? { ...doc, private: variables.body.private } : doc)),
			);
			return { previous };
		},
		onSuccess: (_data, variables) => {
			queryClient.setQueriesData(
				{ queryKey: houseDocumentKeys.list(document.house_id) },
				(oldData: Document[] | undefined) =>
					oldData?.map((doc) => (doc.id === document.id ? { ...doc, private: variables.body.private } : doc)),
			);
		},
		onError: (_err, _vars, context) => {
			context?.previous.forEach(([key, data]) => queryClient.setQueryData(key, data));
			onVisibilityChange?.(document.private ?? false);
			toaster.create({ title: 'Failed to update visibility', type: 'error' });
		},
	});

	const handleVisibilityChange = (isPrivate: boolean) => {
		onVisibilityChange?.(isPrivate);
		updateVisibility({
			path: { house_id: document.house_id, document_id: document.id },
			body: { private: isPrivate },
		});
	};

	return (
		<VStack gap={4} align="stretch">
			{/* Content section - rendered by child components */}
			<Box px={0}>{children}</Box>

			{/* Pending shares choose privacy at accept time (see the drawer's accept actions), not via
			    this visibility PATCH — the document isn't in this house yet. */}
			{canSetPrivate && !isPendingShare && (
				<Panel px={4} py={4}>
					<SharingVisibilitySwitch
						showTitle={true}
						layout="inline"
						titleContent={
							<HStack gap={3}>
								<Icon as={document.private ? LuLock : LuLockOpen} boxSize={5} color="brand.fg" />
								<Title
									level="subsection"
									color="brand.fg"
									css={{ '@media (max-width: 374px)': { fontSize: 'var(--chakra-font-sizes-md)' } }}
								>
									Privacy
								</Title>
							</HStack>
						}
						captionVariant={VisibilityCaptionVariant.Record}
						isPrivate={document.private ?? false}
						onValueChange={handleVisibilityChange}
					/>
				</Panel>
			)}

			{/* Summary section */}
			{showSummary && <DocumentSummary document={document} isPendingShare={isPendingShare} />}

			{/* Tags section */}
			<DocumentTags document={document} isPendingShare={isPendingShare} />

			{/* Details section */}
			<DocumentDetails document={document} />
		</VStack>
	);
};

export default BaseDocumentEdit;
